// frontend/src/hooks/useUsageAnalytics.js
import { useQuery } from '@tanstack/react-query';
import { staffAPI } from '../services/api';

/**
 * Freshness contract for Platform Usage & Adoption (/admin/usage).
 *
 * HR mental model: adoption health check, not a live NOC dashboard.
 * - Volume metrics (clock/leave/docs/logins): ≤5 min stale is fine; refetch
 *   on revisit so "today" in a morning stand-up is trustworthy.
 * - Rhythm heatmaps: historical pattern tool for IT deploy windows; same
 *   cadence is sufficient — the live "Now" pill is UI context only.
 *
 * Both endpoints share a 5-minute server-side Redis cache and the options
 * below. No background polling — HR opens the page when they need it.
 */
export const USAGE_ANALYTICS_STALE_MS = 5 * 60 * 1000;

const usageQueryOptions = {
  staleTime: USAGE_ANALYTICS_STALE_MS,
  refetchOnMount: 'always',
  refetchOnWindowFocus: true,
  keepPreviousData: true,
  retry: (failureCount, error) => {
    if (error?.response?.status === 403) return false;
    return failureCount < 2;
  },
};

/**
 * Platform usage / adoption analytics for the Admin Dashboard.
 *
 * Fetches the volume of every tracked action (clock in/out, leave
 * submissions & approvals, document uploads, OCR runs, audit trail).
 *
 * `range` is either a day count (rolling window) or the string `'all'` for
 * the lifetime / to-date view (month-bucketed, anchored at go-live). It's
 * part of the query key so each window is cached independently.
 */
export const useUsageAnalytics = (range = 30) => {
  const isAll = String(range) === 'all';
  return useQuery({
    queryKey: ['usage', 'analytics', isAll ? 'all' : Number(range)],
    queryFn: async () => {
      const params = isAll ? { range: 'all' } : { days: Number(range) };
      const response = await staffAPI.getUsageAnalytics(params);
      return response.data || null;
    },
    ...usageQueryOptions,
  });
};

/**
 * Employee-behaviour rhythm (weekday × hour by interaction type + segment).
 *
 * `days` and `segment` are both part of the query key so each window/segment
 * combination is cached independently. Returns the full category set; the
 * interaction-type toggle is handled client-side from a single response.
 */
export const useUsageBehavior = (days = 30, segment = 'all') => {
  return useQuery({
    queryKey: ['usage', 'behavior', days, segment],
    queryFn: async () => {
      const response = await staffAPI.getUsageBehavior({ days, segment });
      return response.data || null;
    },
    ...usageQueryOptions,
  });
};

export default useUsageAnalytics;
